import { Chain } from "@rainbow-me/rainbowkit";
import { pick } from "lodash-es";
import { registerInDevtools, Store } from "pullstate";
import { Hash, TransactionReceipt } from "viem";
import { mainnet } from "viem/chains";
import { Theme, themes } from "../../styles/theme";
import { Amount } from "../../types/amount";
import { Order, OrderError } from "../../types/order";
import { OrderStatus } from "../../types/orderStatus";
import { Quote, QuoteError } from "../../types/quote";
import { SignedMessage } from "../../types/signedMessage";
import { WrapQuote, WrapQuoteStatus, WrapStatus } from "../../types/wrapQuote";
import { PermitType } from "../../utils/permitUtils";
import { INITIAL_RECEIVER_STATE } from "../../utils/swapAndSendUtils";
import { initURLParams } from "../../utils/urlUtils";
import { ActivityStore } from "../activity/ActivityStore";
import { Balances } from "./getBalances";
import { Role } from "./getGuildRoles";
import { TokenList } from "./getAllTokenLists";
import { attachReactions } from "./reactions";
import { ApprovalStore } from "../approvals/ApprovalsStore";
import { Allowances } from "./getPermit2Allowance";
import { CoinData } from "../../config/coingecko/coinGeckoTokenList";
import { z } from "zod";
import { ohlcSchema } from "../../generated-schemas-tokens";

export type SelectedToken = MakerToken | TakerToken;
export type MakerToken = TakerToken & WithAllocationPct;
export type MakerTokens = Array<MakerToken>;

export interface TakerToken {
  tokenAddress: Hash;
  amount: Amount;
}

export interface WithAllocationPct {
  pct: number;
  isLocked: boolean;
}

export interface Receiver {
  ensName: string;
  address: string;
  avatar: string;
  userInput: string;
  isAddressLoading: boolean;
}

export type TokenListByChain = { [chainId: Chain["id"]]: TokenList };
export type ChartLists = { [tokenAddress: Hash]: Array<z.infer<typeof ohlcSchema>> };

export interface IGlobalStore {
  address: string;
  allowances: Allowances;
  autoRefreshQuote: boolean;
  balances: Balances;
  ensName: string;
  ensAvatar: string;
  envOverride: "UAT" | "PROD" | undefined;
  zerionNft: string;
  theme: Theme;
  isSending: boolean;
  receiver: Receiver;
  isRequestingQuote: boolean;
  isSliderMoving: boolean;
  isChainSwitching: boolean;
  isFirstQuoteForTrade: boolean;
  makerTokens: MakerTokens;
  modalSelector: string | undefined;
  network: Chain;
  order: Order | undefined;
  orderError: OrderError | undefined;
  orderStatus: OrderStatus;
  quote: Quote | undefined;
  quoteError: QuoteError | undefined;
  receiverError: string;
  quoteExpired: boolean;
  signedMessage: SignedMessage | undefined;
  takerTokens: Array<SelectedToken>;
  tokenLists: TokenListByChain | undefined;
  chartLists: ChartLists | undefined;
  coinGeckoTokenList: Record<Hash, CoinData> | undefined;
  userRoles: Array<Role>;
  wrapQuote: WrapQuote | undefined;
  wrapQuoteStatus: WrapQuoteStatus;
  wrapStatus: WrapStatus;
  permitType: PermitType | undefined;
  permits: {
    permit1: {
      approvalTarget: `0x${string}` | undefined;
      signature: `0x${string}` | undefined;
      deadline: number | undefined;
    };
    permit2: {
      approvalTarget: `0x${string}` | undefined;
      signature: `0x${string}` | undefined;
      deadline: number | undefined;
      nonces: Array<{ token: `0x${string}`; nonce: number }>;
      txData: TransactionReceipt | undefined;
    };
  };

  // UAT-only
  isCustomTokenList: boolean;
  customTokenListUrl: string;
  forceSolver: string | undefined;
}

export const initialWrapQuote = {
  type: "Wrapping",
  amount: undefined,
  max: false,
  gas: Amount.ZERO,
  hash: undefined,
  warning: undefined,
  error: undefined,
} as WrapQuote;

export const INITIAL_STATE = {
  address: "",
  allowances: [],
  autoRefreshQuote: true,
  balances: [],
  ensName: "",
  ensAvatar: "",
  envOverride: undefined,
  theme: themes.dark,
  isRequestingQuote: false,
  isSending: false,
  isChainSwitching: false,
  isSliderMoving: false,
  isFirstQuoteForTrade: true,
  makerTokens: [],
  modalSelector: undefined,
  network: mainnet,
  receiver: INITIAL_RECEIVER_STATE,
  receiverError: "",
  order: undefined,
  orderError: undefined,
  orderStatus: OrderStatus.ENTRY,
  quote: undefined,
  quoteError: undefined,
  quoteExpired: false,
  signedMessage: undefined,
  takerTokens: [],
  tokenLists: undefined,
  chartLists: undefined,
  coinGeckoTokenList: undefined,
  userRoles: [],
  wrapQuote: initialWrapQuote,
  wrapQuoteStatus: WrapQuoteStatus.PENDING_WALLET_CONFIRM,
  wrapStatus: WrapStatus.ENTRY,
  zerionNft: "",
  permitType: undefined,
  permits: {
    permit1: {
      approvalTarget: undefined,
      signature: undefined,
      deadline: undefined,
    },
    permit2: {
      approvalTarget: undefined,
      signature: undefined,
      deadline: undefined,
      nonces: [],
      txData: undefined,
    },
  },

  // UAT-only
  isCustomTokenList: false,
  customTokenListUrl:
    "https://raw.githubusercontent.com/SmolDapp/tokenLists/main/lists/tokenlistooor.json",
  forceSolver: undefined,
};
if (initURLParams) {
  // Needs to be wrapped in an if statement because of jest initialisation order
  initURLParams();
}
export const GlobalStore = new Store<IGlobalStore>(INITIAL_STATE);

/**
 * resetOrderState is used to reset the UI to an initial "Order Entry" state.
 * We reset everything other than the current network, connected wallet and token lists, balances and allowances
 */
export const resetOrderState = (): void => {
  GlobalStore.update(s => {
    const stateToClear = pick(INITIAL_STATE, [
      "autoRefreshQuote",
      "isSending",
      "receiver",
      "isRequestingQuote",
      "isChainSwitching",
      "makerTokens",
      "takerTokens",
      "order",
      "orderError",
      "orderStatus",
      "quote",
      "quoteError",
      "receiverError",
      "quoteExpired",
      "signedMessage",
      "wrapQuote",
      "wrapQuoteStatus",
      "wrapStatus",
      "permits",
      "permitType",
    ]);

    return {
      ...s,
      ...stateToClear,
      takerTokens: [],
    };
  });
};
export const resetWrapState = () => {
  GlobalStore.update(s => {
    const stateToClear = pick(INITIAL_STATE, ["wrapQuote", "wrapStatus"]);

    return {
      ...s,
      ...stateToClear,
    };
  });
};

export const resetUserState = () => {
  const stateToClear = pick(INITIAL_STATE, [
    "address",
    "allowances",
    "balances",
    "ensName",
    "ensAvatar",
    "modalSelector",
    "isTradeDisabled",
    "receiver",
    "zerionNft",
    "userRoles",
  ]);

  GlobalStore.update(s => {
    return {
      ...s,
      ...stateToClear,
    };
  });
};

// Register GlobalStore with Redux Devtools
registerInDevtools({
  GlobalStore: GlobalStore,
  ActivityStore,
  ApprovalStore,
});

attachReactions();
